import { action, observable } from 'mobx'
import type {
  CheckTaxRefundDeliveryMethod,
  TaxRefundDeliveryMethod,
} from './TaxRefundDeliveryMethod'
import { PhysicalAddressEditorViewModel } from '@/features/tax-profile/PhysicalAddressEditorViewModel'
import { validationContext } from 'validx'
import { validateObject, when } from '@/utils/validx-util'

/**
 * View model for editing a check delivery method.
 *
 * If useProfileAddress is true, the mailing address is not required and will be omitted.
 */
export class CheckDeliveryMethodEditorViewModel {
  /**
   * The type of the delivery method.
   */
  readonly type = 'Check' as const

  /**
   * Whether to use the address from the personal/business profile instead of a custom address.
   */
  @observable accessor useProfileAddress = false

  /**
   * View model for editing the mailing address.
   */
  readonly mailingAddressVm = new PhysicalAddressEditorViewModel()

  /**
   * Validation context for the view model.
   * Mailing address is only required if not using the profile address.
   */
  readonly validation = validationContext(this, {
    mailingAddressVm: [when(() => !this.useProfileAddress, validateObject())],
  })

  /**
   * Resets the state of the view model to that of the given
   * check delivery method, or defaults if none is specified.
   *
   * @param method The check delivery method to reset to
   */
  @action.bound
  reset(method?: CheckTaxRefundDeliveryMethod) {
    this.useProfileAddress = !method?.mailingAddress
    this.mailingAddressVm.reset(method?.mailingAddress ?? undefined)
    this.validation.reset()
  }

  /**
   * Maps the state of the VM to {@link CheckTaxRefundDeliveryMethod}.
   */
  toDeliveryMethod(): TaxRefundDeliveryMethod {
    return {
      type: 'Check',
      mailingAddress: this.useProfileAddress
        ? null
        : this.mailingAddressVm.toPhysicalAddress(),
    }
  }
}
